/**
 * StartVoyageFullScreen.js
 *
 * FleetSkipper OS V2: All-in-one voyage start screen with card-grid layout
 *
 * Features:
 * - Card-based layout (2-col on tablet, 1-col on mobile)
 * - Inline FleetSkipper pre-departure checks
 * - Initial weather and fuel logging
 * - "LET GO" creates voyage as UNDERWAY directly
 *
 * FleetSkipper - Built for Mariners.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  MapPin,
  Users,
  Fuel,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Phone,
  Anchor,
  CheckSquare,
  Check,
  Clock,
  Cloud,
  AlertTriangle,
  ShieldX,         // FT-107: Override icon
  ClipboardList,   // Sub-ticket B: Voyage Plan card icon
  AlertCircle      // Sub-ticket B: Resume banner icon
} from 'lucide-react';

// Common components
import Dropdown from '../../../../common/Dropdown';

// Local components
import FuelChecklistItem from './FuelChecklistItem';
import WeatherChecklistItem from './WeatherChecklistItem';
import TemplateFieldsRenderer from './TemplateFieldsRenderer';
import OverrideReasonModal from './OverrideReasonModal';  // FT-107: Compliance override modal
// FT-285 Item 1: TimePickerModal removed — InlineTimeField now lives on the
// LetGoReviewScreen so "Confirm and Let Go" submits with a fresh time snapshot.
import LetGoReviewScreen from './LetGoReviewScreen';  // FT-282: pre-Let-Go review/confirm screen
import VoyagePlanPickerSheet from './VoyagePlanPickerSheet';  // Sub-ticket B: Voyage Plan picker

// Hooks
import { useStorageState } from '../../../../../hooks/useStorageState';
import { useCrew } from '../../../../../hooks/useCrew';
import { useDrills } from '../../../../../hooks/domain/drills/useDrills';  // FT-171: MGN 71 25% rule
import { useVoyages } from '../../../../../hooks/domain/operations/useVoyages';  // FT-269: draft progressive save
import { useVessel } from '../../../../../contexts/VesselContext';  // FT-280: vessel timezone for time picker
import useVoyagePlans from '../../../../../hooks/domain/operations/useVoyagePlans';  // Sub-ticket B: per-vessel plan library
import formatEditedAgo from '../../../../../utils/formatEditedAgo';  // Sub-ticket B: shared "Edited X ago" helper

// Config
import {
  VOYAGE_PURPOSE_LABELS,
  getVoyagePurposeLabels,
  SEA_STATES,
  VISIBILITY_OPTIONS,
  BEAUFORT_SCALE,
  WIND_DIRECTION,
  getOperationTemplate,
  getPhaseOperationChecks  // FT-126: Add this for pre-departure checks
} from '../../../../../config/operationsConfig';
// FT-126: Removed PRE_DEPARTURE_CHECKLIST import (no longer needed)

// FT-07: Pre-departure crew certification gap checking
import { checkCrewCertGaps } from '../../../../../utils/crew/crewCertCheck';
// FT-158: Individual baseline checks (Medical + Sea Survival)
import { checkCrewBaselines } from '../../../../../utils/crew/crewBaselineCheck';
// FT-201: Master qualification and designation
import { getQualifiedMasters, createMasterDesignationSnapshot } from '../../../../../utils/crew/masterQualificationCheck';

/**
 * StartVoyageFullScreen Component
 *
 * PHASE 3: Added template prop for pre-filling form from voyage templates
 * PHASE 3 Step 5: Added onSaveAsPlanned for creating planned voyages
 */
const StartVoyageFullScreen = ({ context, onStart, onCancel, onSaveAsPlanned, template = null }) => {
  const { vessel, currentUser, showAlert } = context;

  // FT-269: progressive save / draft resume hooks.
  // - On mount: if a DRAFT exists for this vessel, prefer that over `template`.
  // - On any form-state change: debounced (1s) write through updateDraftVoyage
  //   so back-navigation / refresh / device-switch always preserves work.
  // - Caller's onStart flow is unchanged — the draft is implicitly promoted
  //   inside useVoyages.startVoyage / startVoyageAndDepart.
  const { draftVoyage, activeVoyage, createDraftVoyage, updateDraftVoyage } = useVoyages();
  // FT-280: vessel context for TimePickerModal timezone default.
  const { currentVessel } = useVessel();
  // Sub-ticket B: per-vessel Voyage Plan library — picker reads activePlans
  // (already filtered to !isArchived). useStorageState scopes by vessel
  // automatically, so the vessel-switcher gets the right library for free.
  const { activePlans } = useVoyagePlans();
  const draftIdRef = useRef(null);
  const draftBootstrappedRef = useRef(false);
  const debouncedSaveRef = useRef(null);
  // FT-278: bootstrap guards so the on-duty / config-driven re-init effects
  // only fire when there is no draft selection to honour.
  const crewBootstrappedRef = useRef(false);
  const checksBootstrappedRef = useRef(false);
  const resumeSeededRef = useRef(false);

  // Load voyage settings
  const { value: voyageSettings } = useStorageState('voyage_settings', null);

  // FT-126: Load pre-departure checks from voyage_settings (same as pre-arrival/post-arrival)
  // This reads from voyage_settings.operationsList[phaseType='pre_departure'].checks
  const preDepartureItems = useMemo(() => {
    return getPhaseOperationChecks('pre_departure', voyageSettings);
  }, [voyageSettings]);

  // Phase 4: Crew selection - get all non-archived crew
  // FT-05: isOnDuty now comes from useCrew hook (which delegates to useFleetCrew)
  const { crew: allCrew, isOnDuty } = useCrew();

  // FT-171: Get drill data for MGN 71 25% rule check
  const { list: drillsData } = useDrills();

  // Phase 4: Selected crew IDs (initialized with on-duty crew)
  const [selectedCrew, setSelectedCrew] = useState([]);

  // FT-201: Selected Master ID and designation snapshot
  const [selectedMasterId, setSelectedMasterId] = useState(null);
  const [masterDesignation, setMasterDesignation] = useState(null);

  // Phase 4: Initialize selected crew with on-duty members when crew data loads
  // FT-07 Fix: Always recalculate from duty status when allCrew changes
  // (removed selectedCrew.length === 0 guard that caused stale selection after duty toggle)
  // FT-278: if a DRAFT exists with saved crew, honour it on first mount so
  // back-out / resume preserves user selections. Otherwise fall back to the
  // on-duty default (one-shot, gated by crewBootstrappedRef).
  useEffect(() => {
    if (!allCrew || allCrew.length === 0) return;

    if (draftVoyage && Array.isArray(draftVoyage.crew) && draftVoyage.crew.length > 0 && !crewBootstrappedRef.current) {
      crewBootstrappedRef.current = true;
      setSelectedCrew(draftVoyage.crew);
      return;
    }

    if (!crewBootstrappedRef.current) {
      crewBootstrappedRef.current = true;
      const onDutyIds = allCrew
        .filter(c => isOnDuty(c) && c.status !== 'archived')
        .map(c => c.id);
      setSelectedCrew(onDutyIds);
    }
  }, [allCrew, draftVoyage]); // eslint-disable-line react-hooks/exhaustive-deps

  // FT-269: bootstrap a DRAFT voyage on mount so back-navigation persists.
  // Resume path: an existing DRAFT for this vessel becomes the active record.
  // Cold-start: if no DRAFT AND no other active voyage, createDraftVoyage()
  // returns a new record we hold by id for subsequent updateDraftVoyage calls.
  // Per brief §24.7: this runs on MOUNT, not on first keystroke.
  useEffect(() => {
    if (draftBootstrappedRef.current) return;
    draftBootstrappedRef.current = true;

    let cancelled = false;
    (async () => {
      if (draftVoyage) {
        draftIdRef.current = draftVoyage.id;
        return;
      }
      // Don't create a draft if another active voyage exists — startVoyage
      // would refuse to promote it anyway. The existing single-active check
      // surfaces an error to the user via the normal LET GO path.
      // FT-277: activeVoyage no longer includes DRAFT; if it's non-null we
      // already have a committed voyage and must not bootstrap a new draft.
      if (activeVoyage) {
        return;
      }
      try {
        const created = await createDraftVoyage({
          from: { name: template?.from?.name || template?.from || vessel?.homePort || '' },
          to: { name: template?.to?.name || template?.to || '' },
          crewCount: template?.crewCount || 0,
          passengerCount: template?.passengerCount || 0,
          purpose: template?.purpose || ''
        });
        if (!cancelled && created?.id) {
          draftIdRef.current = created.id;
        }
      } catch (err) {
        console.warn('[StartVoyageFullScreen] FT-269 draft bootstrap failed (non-fatal):', err);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Form state - pre-fill from template if provided (Phase 3).
  // FT-269: also prefer the existing DRAFT voyage when one exists (resume path).
  // The DRAFT is the canonical seed, then `template`, then sensible defaults.
  // Note: crewCount is now derived from selectedCrew.length
  const initialDraftSeed = draftVoyage; // captured at first render
  const [formData, setFormData] = useState({
    from: initialDraftSeed?.from?.name || template?.from?.name || template?.from || vessel?.homePort || '',
    to: initialDraftSeed?.to?.name || template?.to?.name || template?.to || '',
    crewCount: initialDraftSeed?.crewCount || template?.crewCount || 2,
    passengerCount: initialDraftSeed?.passengerCount || template?.passengerCount || 0,
    purpose: initialDraftSeed?.purpose || template?.purpose || '',
    notes: initialDraftSeed?.notes || template?.notes || '',
    preDepartureNotes: initialDraftSeed?.preDepartureNotes || '',  // FT-150: Pre-departure notes field
    shoreNotified: initialDraftSeed?.shoreNotified || false,
    eta: initialDraftSeed?.eta || ''
  });

  // FT-149: Passenger safety briefing confirmation state
  const [passengerBriefingConfirmed, setPassengerBriefingConfirmed] = useState(false);

  // Weather state
  const [weather, setWeather] = useState({
    windDirection: '',
    windSpeed: '',
    seaState: '',
    visibility: ''
  });

  // Fuel state
  const [fuelData, setFuelData] = useState(null);

  // Pre-departure checks state
  // FT-111: Initialize from preDepartureItems (from useChecklistConfig hook)
  const [checks, setChecks] = useState({});
  const [showOptional, setShowOptional] = useState(false);

  // Template fields - custom fields from phase operation template
  const [templateFieldValues, setTemplateFieldValues] = useState({});

  // FT-111: Initialize checks when preDepartureItems loads.
  // FT-278: merge with any persisted draftChecks on first mount so back-out /
  // resume preserves tick state. Gated by checksBootstrappedRef so config
  // re-renders (or downstream state churn) don't clobber user input.
  useEffect(() => {
    if (!preDepartureItems || preDepartureItems.length === 0) return;
    if (checksBootstrappedRef.current) return;
    checksBootstrappedRef.current = true;

    const draftChecks = draftVoyage?.draftChecks || {};
    const newChecks = {};
    preDepartureItems.forEach(item => {
      if (item.type !== 'fuel') {
        newChecks[item.id] = !!draftChecks[item.id];
      }
    });
    setChecks(newChecks);
  }, [preDepartureItems, draftVoyage]);

  // UI state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // FT-285 Item 1: showTimePicker / pendingTimeOptions state removed.
  // InlineTimeField on the Review screen owns event-time selection now.

  // FT-282: full-screen review/confirm screen between LET GO click and onStart.
  const [showReviewScreen, setShowReviewScreen] = useState(false);

  // FT-107: Compliance override state
  const [overrides, setOverrides] = useState([]);           // Array of override records
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [currentBlocker, setCurrentBlocker] = useState(null);  // Which blocker is being overridden

  // Sub-ticket B: Voyage Plan picker state.
  // - selectedPlanId points at a row in voyage_settings.voyagePlans (via
  //   useVoyagePlans hook). Persisted on the DRAFT as draftSelectedPlanId.
  // - selectedPlanVersionAtSave captures plan.version at PICK time so the
  //   resume banner (B7) can detect drift if admin edited the plan
  //   mid-DRAFT.
  // - planPickerOpen toggles the bottom-sheet picker.
  // Resolved into voyagePlanSnapshot at Let Go by useVoyages.startVoyageAndDepart.
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedPlanVersionAtSave, setSelectedPlanVersionAtSave] = useState(null);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);

  // Sub-ticket B: derived view of the currently selected plan. Defensive
  // against the plan being deleted/archived between selection and render.
  const selectedPlan = useMemo(
    () => selectedPlanId
      ? (Array.isArray(activePlans) ? activePlans.find((p) => p?.id === selectedPlanId) : null)
      : null,
    [activePlans, selectedPlanId]
  );

  // Sub-ticket B (B7): resume banner data. ONLY shown after a resume seed has
  // happened — fresh picks during this session don't trigger drift detection.
  // Returns an object (truthy gate is object-not-number, no render-zero risk)
  // or null.
  const resumePlanDrift = useMemo(() => {
    if (!selectedPlanId) return null;
    if (!resumeSeededRef.current) return null;
    if (selectedPlanVersionAtSave == null) return null;
    if (!Array.isArray(activePlans)) return null;
    const plan = activePlans.find((p) => p?.id === selectedPlanId);
    if (!plan) return null;  // 'source_deleted' surfaces via picker, not banner
    const currentVersion = typeof plan.version === 'number' ? plan.version : 1;
    if (currentVersion > selectedPlanVersionAtSave) {
      return { planName: plan.name, savedVersion: selectedPlanVersionAtSave, currentVersion };
    }
    return null;
  }, [selectedPlanId, activePlans, selectedPlanVersionAtSave]);

  // FT-269: debounced progressive save. Fires 1s after the last form-state
  // change. The hook itself does not debounce (kept stateless), so this is
  // the canonical debounce site per brief §8.2.
  // FT-270 Phase C.7/C.8: declared AFTER every useState referenced in deps /
  // payload so the synchronously-evaluated deps array does not hit a TDZ.
  // FT-278: extended payload + deps to cover the full Start Voyage form
  // (master, weather, fuel, checks map, template fields, passenger briefing,
  // and FT-107 compliance overrides per Jonathan's §11 decision).
  useEffect(() => {
    if (!draftIdRef.current) return;
    if (debouncedSaveRef.current) clearTimeout(debouncedSaveRef.current);
    debouncedSaveRef.current = setTimeout(() => {
      updateDraftVoyage(draftIdRef.current, {
        // Existing FT-269 fields
        from: { name: formData.from },
        to: { name: formData.to },
        crewCount: selectedCrew.length || formData.crewCount || 0,
        crew: selectedCrew,
        passengerCount: formData.passengerCount,
        purpose: formData.purpose,
        notes: formData.notes,
        preDepartureNotes: formData.preDepartureNotes,
        shoreNotified: formData.shoreNotified,
        eta: formData.eta,

        // FT-278 additions
        masterId: selectedMasterId || null,
        masterDesignation: masterDesignation || null,
        draftWeather: weather,
        draftFuelData: fuelData,
        draftChecks: checks,
        draftTemplateFieldValues: templateFieldValues,
        passengerBriefingConfirmed: passengerBriefingConfirmed,
        complianceOverrides: overrides,

        // Sub-ticket B: DRAFT-only plan selection. Round-tripped through
        // useStorageState so resume picks them up via the seeding effect below.
        // Both fields are null until the Master picks a plan.
        draftSelectedPlanId: selectedPlanId,
        draftSelectedPlanVersionAtSave: selectedPlanVersionAtSave
      }).catch(err => console.warn('[StartVoyageFullScreen] progressive save failed (non-fatal):', err));
    }, 1000);
    return () => {
      if (debouncedSaveRef.current) clearTimeout(debouncedSaveRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData,
    selectedCrew,
    selectedMasterId,
    masterDesignation,
    weather,
    fuelData,
    checks,
    templateFieldValues,
    passengerBriefingConfirmed,
    overrides,
    // Sub-ticket B additions
    selectedPlanId,
    selectedPlanVersionAtSave
  ]);

  // FT-278: seed the remaining state hooks from `draftVoyage` on mount.
  // One-shot via resumeSeededRef so post-mount draft mutations (echo from
  // our own debounced save) don't overwrite live user input.
  // Placed AFTER all referenced useState calls so setters resolve.
  useEffect(() => {
    if (resumeSeededRef.current) return;
    if (!draftVoyage) return;
    resumeSeededRef.current = true;

    if (draftVoyage.masterId) setSelectedMasterId(draftVoyage.masterId);
    if (draftVoyage.masterDesignation) setMasterDesignation(draftVoyage.masterDesignation);
    if (draftVoyage.draftWeather) setWeather({
      windDirection: draftVoyage.draftWeather.windDirection || '',
      windSpeed: draftVoyage.draftWeather.windSpeed || '',
      seaState: draftVoyage.draftWeather.seaState || '',
      visibility: draftVoyage.draftWeather.visibility || ''
    });
    if (draftVoyage.draftFuelData) setFuelData(draftVoyage.draftFuelData);
    if (draftVoyage.draftTemplateFieldValues) setTemplateFieldValues(draftVoyage.draftTemplateFieldValues);
    if (typeof draftVoyage.passengerBriefingConfirmed === 'boolean') {
      setPassengerBriefingConfirmed(draftVoyage.passengerBriefingConfirmed);
    }
    // FT-278 (Jonathan's §11 decision): rehydrate FT-107 compliance overrides
    // so the user does not have to re-acknowledge after back-out / resume.
    if (Array.isArray(draftVoyage.complianceOverrides)) setOverrides(draftVoyage.complianceOverrides);

    // Sub-ticket B: rehydrate plan selection from DRAFT. Defensive null/type
    // guards because pre-B DRAFTs (none in production) lack these fields.
    if (draftVoyage.draftSelectedPlanId) {
      setSelectedPlanId(draftVoyage.draftSelectedPlanId);
    }
    if (typeof draftVoyage.draftSelectedPlanVersionAtSave === 'number') {
      setSelectedPlanVersionAtSave(draftVoyage.draftSelectedPlanVersionAtSave);
    }
  }, [draftVoyage]);

  // Purpose options for dropdown - filtered by settings (uses getVoyagePurposeLabels for enabled filtering)
  const purposeOptions = useMemo(() => {
    const options = [{ value: '', label: 'Select purpose (optional)' }];

    // Get enabled purposes from settings (uses purposesList with enabled flag)
    const enabledPurposes = getVoyagePurposeLabels(voyageSettings);

    // Add purposes that are enabled
    Object.entries(enabledPurposes).forEach(([value, label]) => {
      options.push({ value, label });
    });

    options.push({ value: 'other', label: 'Other (specify)...' });
    return options;
  }, [voyageSettings]);

  // Get saved ports for autocomplete
  const savedPorts = useMemo(() => {
    return voyageSettings?.savedPorts || [];
  }, [voyageSettings]);

  // FT-126: Get check lists from voyage_settings via getPhaseOperationChecks()
  // All items are considered required (Phase 9 decision - no required/optional toggle)
  const requiredChecks = useMemo(() => {
    // Filter out fuel type items (handled separately by FuelChecklistItem)
    return (preDepartureItems || []).filter(c => c.type !== 'fuel');
  }, [preDepartureItems]);

  // FT-126: No optional checks - all items are required per Phase 9 decision
  const optionalChecks = useMemo(() => [], []);

  // Get custom template fields for pre_departure phase
  const templateFields = useMemo(() => {
    const template = getOperationTemplate('pre_departure', voyageSettings);
    return template?.fields || [];
  }, [voyageSettings]);

  // Handler for template field changes
  const handleTemplateFieldChange = useCallback((fieldId, value) => {
    setTemplateFieldValues(prev => ({ ...prev, [fieldId]: value }));
  }, []);

  // Calculate completion status - derive required IDs from requiredChecks memo
  const requiredCheckIds = useMemo(() => {
    // Use the requiredChecks memo which already handles phase operations vs legacy
    return requiredChecks.map(c => c.id);
  }, [requiredChecks]);
  const requiredCompleted = requiredCheckIds.filter(id => checks[id]).length;
  const allRequiredChecksComplete = requiredCompleted === requiredCheckIds.length;
  const optionalCompleted = optionalChecks.filter(c => checks[c.id]).length;

  // FT-149: Passenger briefing is required if passengers > 0
  const passengerBriefingRequired = formData.passengerCount > 0;
  const passengerBriefingComplete = !passengerBriefingRequired || passengerBriefingConfirmed;

  // FT-07: Check for crew certification gaps (AT_LEAST_1 requirements)
  // FT-07 Fix: Use selectedCrewKey to force recalculation when crew selection changes
  // This ensures warnings recalculate when crew are deselected (React array comparison gotcha)
  const selectedCrewKey = JSON.stringify([...selectedCrew].sort());
  const crewCertGaps = useMemo(() => {
    if (!vessel || selectedCrew.length === 0 || !allCrew) return [];

    // Get full crew objects for selected IDs
    const selectedCrewMembers = selectedCrew
      .map(id => allCrew.find(c => c.id === id))
      .filter(Boolean);

    // Build vessel settings object for cert check
    // FT-30 Fix: Include all equipment flags needed by checkCrewCertGaps
    // FT-158: Added singleHandedEndorsed for manning checks
    const vesselSettings = {
      lengthOverall: vessel.lengthOverall || vessel.length || 0,
      category: vessel.category || vessel.vesselCategory || 'cat6',
      enginePowerKW: vessel.enginePowerKW || vessel.enginePower || 0,
      // FT-158: Single-handed endorsement for Cat 3-6
      singleHandedEndorsed: vessel.singleHandedEndorsed || false,
      // Equipment flags for conditional cert requirements
      hasRadar: vessel.hasRadar,
      hasECDIS: vessel.hasECDIS,
      hasCooking: vessel.hasCooking,
      hasGalley: vessel.hasGalley,
      hasStabilityBooklet: vessel.hasStabilityBooklet,
      // Nested objects
      optionalEquipment: vessel.optionalEquipment,
      navcomms: vessel.navcomms,
      accommodation: vessel.accommodation,
      navigationEquipment: vessel.navigationEquipment || [],
      operations: vessel.operations
    };

    return checkCrewCertGaps(selectedCrewMembers, vesselSettings);
  }, [vessel, selectedCrewKey, allCrew]); // eslint-disable-line react-hooks/exhaustive-deps

  // FT-158: Check individual baselines (Medical + Sea Survival for ALL crew)
  const crewBaselineGaps = useMemo(() => {
    if (selectedCrew.length === 0 || !allCrew) return { valid: true, failures: [] };

    const selectedCrewMembers = selectedCrew
      .map(id => allCrew.find(c => c.id === id))
      .filter(Boolean);

    return checkCrewBaselines(selectedCrewMembers);
  }, [selectedCrewKey, allCrew]); // eslint-disable-line react-hooks/exhaustive-deps

  // FT-171: MGN 71 25% Rule - Check drill participation for previous calendar month
  // MGN 71 5.2: "Each crew member must participate in at least one abandon ship drill
  // and one fire drill every month. These drills must be held within 24 hours of
  // leaving port if more than 25% of the crew have not taken part in drills on
  // board the ship in the previous month."
  const drillParticipationGaps = useMemo(() => {
    if (selectedCrew.length === 0 || !drillsData || drillsData.length === 0) return [];

    const warnings = [];
    const drillTypesToCheck = [
      { type: 'fire', displayName: 'Fire Drill' },
      { type: 'abandon_ship', displayName: 'Abandon Ship Drill' }
    ];

    // FT-179: Use rolling 30 days instead of previous calendar month
    // Previous logic showed false warnings for new users who completed drills THIS month
    const cutoff = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

    drillTypesToCheck.forEach(({ type, displayName }) => {
      // Get drills of this type from last 30 days
      const recentDrills = drillsData.filter(drill => {
        const drillDate = new Date(drill.completedAt || drill.date);
        return (
          drill.type === type &&
          drillDate >= cutoff &&
          drill.status === 'completed'
        );
      });

      // Collect all participant IDs from those drills
      const participantIds = new Set();
      recentDrills.forEach(drill => {
        (drill.participants || []).forEach(p => {
          // Handle both { id, name } objects and plain string IDs
          const pId = typeof p === 'string' ? p : p.id;
          if (pId) participantIds.add(pId);
        });
      });

      // Count how many selected crew participated
      const crewWhoParticipated = selectedCrew.filter(id => participantIds.has(id));
      const participationRate = crewWhoParticipated.length / selectedCrew.length;

      // MGN 71 rule: warn if >25% have NOT participated (i.e., <75% participated)
      if (participationRate < 0.75) {
        const nonParticipantCount = selectedCrew.length - crewWhoParticipated.length;
        const percentage = Math.round((nonParticipantCount / selectedCrew.length) * 100);

        warnings.push({
          drillType: type,
          displayName,
          participatedCount: crewWhoParticipated.length,
          totalCrew: selectedCrew.length,
          nonParticipantCount,
          percentage,
          message: `${nonParticipantCount} of ${selectedCrew.length} crew (${percentage}%) have not participated in a ${displayName} in the last 30 days`
        });
      }
    });

    return warnings;
  }, [selectedCrewKey, drillsData]); // eslint-disable-line react-hooks/exhaustive-deps

  // FT-201: Get qualified masters from selected crew
  // Only include crew members who are both selected AND qualified to be Master
  const qualifiedMasters = useMemo(() => {
    if (!allCrew || selectedCrew.length === 0) return [];
    const selectedCrewMembers = allCrew.filter(c => selectedCrew.includes(c.id));
    return getQualifiedMasters(selectedCrewMembers, vessel);
  }, [allCrew, selectedCrew, vessel]);

  // FT-201: Transform qualified masters into Dropdown options format
  const masterOptions = useMemo(() => {
    return qualifiedMasters.map(({ crew, qualificationResult }) => {
      const cocCert = qualificationResult.qualifyingCerts?.find(c => c.type === 'coc');
      const certBasis = cocCert?.cert?.type || cocCert?.cert?.title || 'Qualified';
      return {
        value: crew.id,
        label: `${crew.name || crew.full_name} — ${certBasis}`
      };
    });
  }, [qualifiedMasters]);

  // FT-201: Handle Master selection - creates immutable qualification snapshot
  const handleMasterSelection = useCallback((masterId) => {
    setSelectedMasterId(masterId);
    if (masterId) {
      const master = allCrew.find(c => c.id === masterId);
      if (master) {
        const snapshot = createMasterDesignationSnapshot(master, vessel, currentUser?.id);
        setMasterDesignation(snapshot);
        console.log('[StartVoyage] Master designated:', { masterId, masterName: master.name, snapshot });
      }
    } else {
      setMasterDesignation(null);
    }
  }, [allCrew, vessel, currentUser]);

  // FT-107: Critical cert gap types that MUST block departure (per WBC3 detention risk)
  const CRITICAL_CERT_GAPS = ['Master/Skipper', 'Radio Operator', 'Minimum Manning', 'Second Person Qualification'];

  // FT-107: Check if a baseline failure has been overridden
  const isBaselineOverridden = useCallback((crewId, missingCert) => {
    return overrides.some(o =>
      o.blockerType === 'baseline' &&
      o.crewId === crewId &&
      o.blockerKey === missingCert
    );
  }, [overrides]);

  // FT-107: Check if a collective cert gap has been overridden
  const isCollectiveOverridden = useCallback((certName) => {
    return overrides.some(o =>
      o.blockerType === 'collective' &&
      o.blockerKey === certName
    );
  }, [overrides]);

  // FT-107: Calculate unresolved baseline failures (not overridden)
  const unresolvedBaselineFailures = useMemo(() => {
    return crewBaselineGaps.failures.filter(f =>
      f.missing.some(cert => !isBaselineOverridden(f.crewId, cert))
    );
  }, [crewBaselineGaps.failures, isBaselineOverridden]);

  // FT-107: Calculate unresolved critical cert gaps (not overridden)
  const unresolvedCriticalGaps = useMemo(() => {
    return crewCertGaps.filter(gap =>
      CRITICAL_CERT_GAPS.includes(gap.cert) && !isCollectiveOverridden(gap.cert)
    );
  }, [crewCertGaps, isCollectiveOverridden]);

  // Can proceed validation (for LET GO - requires all checks complete)
  // Phase 4: Use selectedCrew.length instead of formData.crewCount
  // FT-149: Also requires passenger briefing if passengers > 0
  // FT-158: Also requires baseline certs (Medical + Sea Survival) for all crew
  // FT-107: Now blocks on critical cert gaps, allows override
  const canProceed = useMemo(() => {
    // FT-107: All baseline failures must be resolved OR overridden
    const baselinesResolved = unresolvedBaselineFailures.length === 0;
    // FT-107: All critical cert gaps must be resolved OR overridden
    const criticalGapsResolved = unresolvedCriticalGaps.length === 0;

    return (
      formData.from.trim() !== '' &&
      formData.to.trim() !== '' &&
      selectedCrew.length >= 1 &&
      allRequiredChecksComplete &&
      fuelData !== null &&
      passengerBriefingComplete &&
      baselinesResolved &&       // FT-107: Baselines valid OR overridden
      criticalGapsResolved       // FT-107: Critical gaps valid OR overridden
    );
  }, [formData, allRequiredChecksComplete, fuelData, selectedCrew, passengerBriefingComplete, unresolvedBaselineFailures.length, unresolvedCriticalGaps.length]);

  // Update form field
  const updateField = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  }, [errors]);

  // Sub-ticket B: pick a Voyage Plan. Auto-pre-fills Route From/To when the
  // plan has defaults AND the form field is currently EMPTY (never overwrites
  // typed input). Snapshots plan.version at pick time so the resume banner
  // can detect drift if admin edits the plan mid-DRAFT.
  // Pass `null` to clear the selection.
  const handlePickPlan = useCallback((planId) => {
    setSelectedPlanId(planId);
    if (!planId) {
      setSelectedPlanVersionAtSave(null);
      return;
    }
    const plan = Array.isArray(activePlans)
      ? activePlans.find((p) => p?.id === planId)
      : null;
    if (!plan) return;

    setFormData(prev => ({
      ...prev,
      from: (typeof prev.from === 'string' && prev.from.trim().length > 0)
        ? prev.from
        : (plan.defaultFrom || prev.from),
      to: (typeof prev.to === 'string' && prev.to.trim().length > 0)
        ? prev.to
        : (plan.defaultTo || prev.to),
      // Pre-fill purpose only if Master hasn't picked one yet
      purpose: (typeof prev.purpose === 'string' && prev.purpose.trim().length > 0)
        ? prev.purpose
        : (plan.defaultPurpose || prev.purpose)
    }));

    setSelectedPlanVersionAtSave(typeof plan.version === 'number' ? plan.version : 1);
  }, [activePlans]);

  // Sub-ticket B (B7): acknowledge plan drift — advance the captured version
  // to the current library version so the resume banner dismisses. v1 has
  // no diff UI; the Master either accepts the latest plan or picks a
  // different one via the picker.
  const handleAcceptPlanDrift = useCallback(() => {
    if (!selectedPlanId) return;
    const plan = Array.isArray(activePlans)
      ? activePlans.find((p) => p?.id === selectedPlanId)
      : null;
    if (!plan) return;
    setSelectedPlanVersionAtSave(typeof plan.version === 'number' ? plan.version : 1);
  }, [activePlans, selectedPlanId]);

  // Update weather field
  const updateWeather = useCallback((field, value) => {
    setWeather(prev => ({ ...prev, [field]: value }));
  }, []);

  // Toggle check
  const toggleCheck = useCallback((checkId) => {
    setChecks(prev => ({ ...prev, [checkId]: !prev[checkId] }));
  }, []);

  // Handle fuel change
  const handleFuelChange = useCallback((fuel) => {
    setFuelData(fuel);
  }, []);

  // Phase 4: Toggle crew selection
  const toggleCrewSelection = useCallback((crewId) => {
    setSelectedCrew(prev => {
      if (prev.includes(crewId)) {
        return prev.filter(id => id !== crewId);
      } else {
        return [...prev, crewId];
      }
    });
  }, []);

  // FT-107: Get skipper name from selected crew (for override acknowledgment)
  const skipperName = useMemo(() => {
    if (!allCrew || selectedCrew.length === 0) return null;
    const master = selectedCrew
      .map(id => allCrew.find(c => c.id === id))
      .find(c => c && (c.position === 'Master' || c.position === 'Skipper'));
    return master?.name || master?.full_name || currentUser?.name || 'Master';
  }, [allCrew, selectedCrew, currentUser]);

  // FT-107: Open override modal for a specific blocker
  const handleOpenOverride = useCallback((blocker) => {
    setCurrentBlocker(blocker);
    setOverrideModalOpen(true);
  }, []);

  // FT-107: Handle override submission
  const handleSubmitOverride = useCallback((overrideData) => {
    if (!currentBlocker) return;

    const newOverride = {
      id: `override-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
      blockerType: currentBlocker.type,        // 'baseline' | 'collective'
      blockerKey: currentBlocker.key,          // e.g., 'Medical Fitness (ML5/ENG1)' or 'Master/Skipper'
      blockerName: currentBlocker.name,        // Human-readable name
      crewId: currentBlocker.crewId || null,
      crewName: currentBlocker.crewName || null,
      wbc3Ref: currentBlocker.wbc3Ref || null,
      reason: overrideData.reason,
      dispensationRef: overrideData.dispensationRef,
      acknowledgedBy: overrideData.acknowledgedBy,
      timestamp: overrideData.timestamp
    };

    setOverrides(prev => [...prev, newOverride]);
    setOverrideModalOpen(false);
    setCurrentBlocker(null);
  }, [currentBlocker]);

  // Validate form
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.from.trim()) {
      newErrors.from = 'Departure location is required';
    }

    if (!formData.to.trim()) {
      newErrors.to = 'Destination is required';
    }

    // Phase 4: Validate selected crew instead of manual count
    if (selectedCrew.length < 1) {
      newErrors.crewCount = 'Select at least 1 crew member';
    }

    // FT-282 (locked decision): Master must be designated before Let Go.
    // Surfaced inline near the Master designation dropdown (line ~999 errors.master).
    if (!selectedMasterId) {
      newErrors.master = 'Master must be designated before letting go';
    }

    if (!fuelData) {
      newErrors.fuel = 'Departure fuel level required';
    }

    if (!allRequiredChecksComplete) {
      newErrors.checks = 'Complete all required checks';
    }

    // FT-149: Check passenger briefing if passengers > 0
    if (formData.passengerCount > 0 && !passengerBriefingConfirmed) {
      newErrors.passengerBriefing = 'Confirm passenger safety briefing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, fuelData, allRequiredChecksComplete, selectedCrew, selectedMasterId, passengerBriefingConfirmed]);

  // FT-282 §4.2: validate then show review screen. Replaces FT-280's direct
  // TimePickerModal open. The review screen's "Confirm and Let Go" CTA opens
  // the TimePickerModal (via handleConfirmAndLetGo below).
  const handleLetGo = useCallback(() => {
    if (!validateForm()) return;
    setShowReviewScreen(true);
  }, [validateForm]);

  // FT-282 §4.2: review screen "Edit" — back to form, all state intact.
  const handleEditFromReview = useCallback(() => {
    setShowReviewScreen(false);
  }, []);

  // FT-285 Item 1: Review screen "Confirm and Let Go" now passes a fresh
  // timeOptions snapshot from its InlineTimeField. We submit immediately —
  // no intermediate TimePickerModal step. On failure, the review screen is
  // re-opened so the Master can retry. The FT-282 "cancel returns to form"
  // semantics are now reached via the Review screen's Edit button.
  const handleConfirmAndLetGo = useCallback(async (timeOptions) => {
    setShowReviewScreen(false);
    setIsSubmitting(true);
    try {
      const weatherData = (weather.windDirection || weather.windSpeed || weather.seaState || weather.visibility)
        ? {
            windDirection: weather.windDirection || null,
            windSpeed: weather.windSpeed || null,
            seaState: weather.seaState || null,
            visibility: weather.visibility || null
          }
        : null;

      const voyageFormData = {
        ...formData,
        crew: selectedCrew,
        crewCount: selectedCrew.length,
        crewNames: selectedCrew
          .map(id => allCrew.find(c => c.id === id))
          .filter(Boolean)
          .map(c => c.name || c.full_name || 'Unknown'),
        preDepartureFields: Object.keys(templateFieldValues).length > 0 ? templateFieldValues : undefined,
        passengerBriefingConfirmed: passengerBriefingConfirmed,
        preDepartureNotes: formData.preDepartureNotes || '',
        complianceOverrides: overrides.length > 0 ? overrides : undefined,
        masterId: selectedMasterId || undefined,
        masterDesignation: masterDesignation || undefined
      };

      // Forward timeOptions as the 5th positional arg (unchanged contract).
      await onStart(voyageFormData, checks, weatherData, fuelData, timeOptions);
    } catch (error) {
      console.error('Failed to start voyage:', error);
      setErrors({ submit: 'Failed to start voyage. Please try again.' });
      // Re-open the review screen so the Master sees the error and can retry.
      setShowReviewScreen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, checks, weather, fuelData, onStart, selectedCrew, allCrew, templateFieldValues, passengerBriefingConfirmed, overrides, selectedMasterId, masterDesignation]);

  // Render a check item
  // FT-110/FT-111: Removed "Required" badge - per Phase 9, all items are required by definition
  const renderCheckItem = (check) => (
    <button
      key={check.id}
      onClick={() => toggleCheck(check.id)}
      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
        checks[check.id]
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-cyan-300'
      }`}
      style={{ minHeight: '56px' }}
    >
      <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center ${
        checks[check.id]
          ? 'bg-green-500 border-green-500'
          : 'border-cyan-400'
      }`}>
        {checks[check.id] && <Check className="h-4 w-4 text-white" />}
      </div>

      <div className="flex-1 min-w-0">
        <p className={`font-medium ${checks[check.id] ? 'text-green-700 line-through' : 'text-gray-900'}`}>
          {check.text}
        </p>
      </div>
    </button>
  );

  // FT-282: render gate — review screen is a full-screen takeover between
  // the form and the TimePickerModal. All form state is preserved while it
  // is mounted; Edit returns here untouched.
  if (showReviewScreen) {
    return (
      <LetGoReviewScreen
        formData={formData}
        selectedCrew={selectedCrew}
        allCrew={allCrew}
        selectedMasterId={selectedMasterId}
        masterDesignation={masterDesignation}
        weather={weather}
        fuelData={fuelData}
        checks={checks}
        requiredChecks={requiredChecks}
        passengerBriefingConfirmed={passengerBriefingConfirmed}
        overrides={overrides}
        vessel={vessel}
        // FT-285 Item 1: Review screen owns the InlineTimeField; pass the
        // resolved timezone (same fallback chain the old TimePickerModal
        // mount used) so the badge displays in the vessel's TZ.
        vesselTimezone={
          currentVessel?.settings?.timezone
          || currentVessel?.timezone
          || vessel?.timezone
          || 'UTC'
        }
        isSubmitting={isSubmitting}
        onEdit={handleEditFromReview}
        onConfirm={handleConfirmAndLetGo}
      />
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="p-2 -ml-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-gray-900">New Voyage</h1>
            <p className="text-sm text-gray-500">Complete checks and let go</p>
          </div>
        </div>
      </div>

      {/* Form Content - Scrollable */}
      <div className="flex-1 overflow-auto">
        <div className="p-4 max-w-4xl mx-auto">

          {/* Card Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* ============================================ */}
            {/* VOYAGE PLAN PICKER CARD — Sub-ticket B       */}
            {/* FIRST card per synthesis C1 (LOCKED).        */}
            {/* Full width on tablet because picking a plan  */}
            {/* affects the Route card below.                */}
            {/* ============================================ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 md:col-span-2">

              {/* Resume banner (B7) — render gate is object-truthy, not
                  number-truthy, so no render-zero risk. */}
              {resumePlanDrift ? (
                <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-amber-900">
                      This plan was updated since you selected it.
                    </p>
                    <button
                      type="button"
                      onClick={handleAcceptPlanDrift}
                      className="mt-2 text-sm font-medium text-amber-700 hover:text-amber-800 underline"
                      data-testid="voyage-plan-drift-accept"
                    >
                      Continue with latest
                    </button>
                  </div>
                </div>
              ) : null}

              <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-3">
                <ClipboardList className="h-5 w-5 text-gray-400" />
                Voyage Plan
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  Optional
                </span>
              </h3>

              {/* No plan attached: single CTA */}
              {!selectedPlan ? (
                <>
                  <p className="text-sm text-gray-500 mb-3">
                    No plan attached — tap to pick a plan.
                  </p>
                  <button
                    type="button"
                    onClick={() => setPlanPickerOpen(true)}
                    className="w-full min-h-[56px] flex items-center justify-center px-4 py-3 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium text-cyan-700"
                    data-testid="voyage-plan-picker-open"
                  >
                    Pick a Voyage Plan
                  </button>
                </>
              ) : (
                <>
                  {/* Plan summary row */}
                  <div className="mb-3">
                    <h4 className="text-sm font-semibold text-gray-900 truncate">
                      {selectedPlan.name || 'Untitled plan'}
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {(() => {
                        const c = Array.isArray(selectedPlan.items) ? selectedPlan.items.length : 0;
                        return c === 1 ? '1 item' : `${c} items`;
                      })()} · {formatEditedAgo(selectedPlan.updatedAt)}
                    </p>
                  </div>

                  {/* Compact preview of first 3-5 items (read-only) */}
                  {Array.isArray(selectedPlan.items) && selectedPlan.items.length > 0 ? (
                    <ol className="mb-3 space-y-1 text-sm text-gray-700">
                      {selectedPlan.items.slice(0, 5).map((it, i) => {
                        const icon = it?.kind === 'operation_ref' ? 'OP' : 'WP';
                        const label = it?.kind === 'waypoint'
                          ? (it.label || 'Waypoint')
                          : (it.displayLabel || 'Operation');
                        return (
                          <li key={it?.id || i} className="flex items-start gap-2">
                            <span className="text-xs font-mono text-gray-400 mt-0.5 shrink-0">
                              {i + 1}.
                            </span>
                            <span className="text-xs font-semibold text-gray-500 shrink-0 mt-0.5">
                              [{icon}]
                            </span>
                            <span className="flex-1 min-w-0 truncate">{label}</span>
                            {/* instructionNote rendered with !! guard */}
                            {!!it?.instructionNote && (
                              <span className="text-xs text-gray-400 truncate ml-2">
                                — {it.instructionNote}
                              </span>
                            )}
                          </li>
                        );
                      })}
                      {selectedPlan.items.length > 5 ? (
                        <li className="text-xs text-gray-400 pl-6">
                          + {selectedPlan.items.length - 5} more
                        </li>
                      ) : null}
                    </ol>
                  ) : null}

                  {/* Action row */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPlanPickerOpen(true)}
                      className="flex-1 min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                      data-testid="voyage-plan-card-change"
                    >
                      Change plan
                    </button>
                    <button
                      type="button"
                      onClick={() => handlePickPlan(null)}
                      className="flex-1 min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50"
                      data-testid="voyage-plan-card-clear"
                    >
                      Clear plan
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* ============================================ */}
            {/* ROUTE CARD */}
            {/* ============================================ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                <MapPin className="h-5 w-5 text-gray-400" />
                Route
              </h3>

              {/* From */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.from}
                  onChange={(e) => updateField('from', e.target.value)}
                  placeholder="Departure location"
                  list="saved-ports-list"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${errors.from ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {errors.from && (
                  <p className="mt-1 text-sm text-red-500">{errors.from}</p>
                )}
              </div>

              {/* To */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  To <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.to}
                  onChange={(e) => updateField('to', e.target.value)}
                  placeholder="Destination"
                  list="saved-ports-list"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 ${errors.to ? 'border-red-500' : 'border-gray-300'
                    }`}
                />
                {errors.to && (
                  <p className="mt-1 text-sm text-red-500">{errors.to}</p>
                )}
              </div>

              {/* Purpose */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Purpose
                </label>
                <Dropdown
                  value={formData.purpose}
                  onChange={(value) => updateField('purpose', value)}
                  options={purposeOptions}
                  width="w-full"
                />

                {/* Purpose "Other" specification */}
                {formData.purpose === 'other' && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specify Purpose
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Sea trials, Training exercise, Towing..."
                      value={formData.purposeOther || ''}
                      onChange={(e) => updateField('purposeOther', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                    />
                  </div>
                )}
              </div>

              {/* Saved Ports Datalist for autocomplete */}
              {savedPorts.length > 0 && (
                <datalist id="saved-ports-list">
                  {savedPorts.map((port, index) => (
                    <option key={index} value={port} />
                  ))}
                </datalist>
              )}
            </div>

            {/* ============================================ */}
            {/* CREW & PASSENGERS CARD - Phase 4: Crew Selection */}
            {/* ============================================ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                <Users className="h-5 w-5 text-gray-400" />
                Crew & Passengers
              </h3>

              {/* Phase 4: Crew Selection */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Select Crew On Board <span className="text-red-500">*</span>
                  </label>
                  <span className={`text-sm font-medium ${selectedCrew.length > 0 ? 'text-green-600' : 'text-amber-600'}`}>
                    {selectedCrew.length} selected
                  </span>
                </div>

                {/* Crew List */}
                {allCrew && allCrew.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {allCrew
                      .filter(c => c.status !== 'archived')
                      .map(crewMember => {
                        const isSelected = selectedCrew.includes(crewMember.id);
                        const crewName = crewMember.name || crewMember.full_name || 'Unknown';
                        const role = crewMember.role || crewMember.position || '';
                        const onDuty = isOnDuty(crewMember);

                        return (
                          <button
                            key={crewMember.id}
                            onClick={() => toggleCrewSelection(crewMember.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                              isSelected
                                ? 'bg-cyan-50 border-cyan-300'
                                : 'bg-white border-gray-200 hover:border-cyan-300'
                            }`}
                          >
                            {/* Checkbox */}
                            <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                              isSelected
                                ? 'bg-cyan-500 border-cyan-500'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && <Check className="h-3 w-3 text-white" />}
                            </div>

                            {/* Name and Role */}
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium text-sm ${isSelected ? 'text-cyan-700' : 'text-gray-900'}`}>
                                {crewName}
                              </p>
                              {role && (
                                <p className="text-xs text-gray-500 truncate">{role}</p>
                              )}
                            </div>

                            {/* Duty Status Badge */}
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              onDuty
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {onDuty ? 'on duty' : 'off duty'}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm border border-gray-200 rounded-lg">
                    No crew members found. Add crew in the Crew module.
                  </div>
                )}

                {errors.crewCount && (
                  <p className="mt-1 text-sm text-red-500">{errors.crewCount}</p>
                )}

                {/* FT-201: Master Designation Dropdown */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Designate Master <span className="text-red-500">*</span>
                    </label>
                    {selectedMasterId && (
                      <span className="text-xs text-green-600 flex items-center gap-1">
                        <Check className="h-3 w-3" /> Qualified
                      </span>
                    )}
                  </div>

                  {qualifiedMasters.length > 0 ? (
                    <Dropdown
                      value={selectedMasterId || ''}
                      onChange={(value) => handleMasterSelection(value || null)}
                      options={masterOptions}
                      placeholder="Select Master..."
                      width="w-full"
                    />
                  ) : selectedCrew.length > 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                      <AlertTriangle className="h-4 w-4 inline mr-2" />
                      No qualified Masters among selected crew. Check certificates (CoC + Medical + Survival required).
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm italic">
                      Select crew first to designate Master
                    </div>
                  )}

                  {errors.master && (
                    <p className="mt-1 text-sm text-red-500">{errors.master}</p>
                  )}
                </div>

                {/* FT-158 + FT-107: Individual Baseline Failures (BLOCKING - Red with Override) */}
                {!crewBaselineGaps.valid && crewBaselineGaps.failures.map(failure => (
                  failure.missing.map(cert => {
                    const isOverridden = isBaselineOverridden(failure.crewId, cert);
                    return (
                      <div
                        key={`${failure.crewId}-${cert}`}
                        className={`mt-3 border-l-4 p-4 rounded-lg ${
                          isOverridden
                            ? 'bg-green-50 border-green-400'
                            : 'bg-red-50 border-red-400'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start flex-1">
                            {isOverridden ? (
                              <Check className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                            ) : (
                              <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                            )}
                            <div>
                              <p className={`font-medium ${isOverridden ? 'text-green-800' : 'text-red-800'}`}>
                                {failure.crewName}: Missing {cert}
                              </p>
                              <p className={`text-xs mt-1 italic ${isOverridden ? 'text-green-600' : 'text-red-600'}`}>
                                WBC3 28.1.4/28.1.5
                              </p>
                              {isOverridden && (
                                <p className="text-xs text-green-700 mt-1">
                                  ✓ Overridden — see voyage record for details
                                </p>
                              )}
                            </div>
                          </div>
                          {!isOverridden && (
                            <button
                              onClick={() => handleOpenOverride({
                                type: 'baseline',
                                key: cert,
                                name: `${failure.crewName}: ${cert}`,
                                crewId: failure.crewId,
                                crewName: failure.crewName,
                                wbc3Ref: 'WBC3 28.1.4/28.1.5'
                              })}
                              className="ml-3 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg flex items-center gap-1"
                            >
                              <ShieldX className="h-3.5 w-3.5" />
                              Override
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                ))}

                {/* FT-171: MGN 71 25% Rule - Drill Participation Advisory (Amber - warning, not blocking) */}
                {drillParticipationGaps.length > 0 && (
                  <div className="mt-3 bg-amber-50 border-l-4 border-amber-400 p-4 rounded-lg">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-amber-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div>
                        <h4 className="font-medium text-amber-800">Drill Participation Advisory</h4>
                        <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                          {drillParticipationGaps.map((gap, i) => (
                            <li key={i}>{gap.message}</li>
                          ))}
                        </ul>
                        <p className="text-xs text-amber-600 mt-2 italic">
                          MGN 71 5.2: Drills must be held within 24 hours of leaving port. If not practical,
                          musters and emergency duty instructions must be held within this period,
                          with full drills as soon as circumstances permit.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* FT-07 + FT-107: Crew Certification Gaps
                    - Critical gaps (Master, Radio, Manning, Second Person): Red with Override button
                    - Non-critical gaps: Amber warnings only */}
                {crewCertGaps.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {crewCertGaps.map((gap, index) => {
                      const isCritical = CRITICAL_CERT_GAPS.includes(gap.cert);
                      const isOverridden = isCritical && isCollectiveOverridden(gap.cert);

                      // Choose colors based on critical + overridden status
                      const bgColor = isOverridden
                        ? 'bg-green-50 border-green-300'
                        : isCritical
                          ? 'bg-red-50 border-red-300'
                          : 'bg-amber-50 border-amber-200';
                      const iconColor = isOverridden
                        ? 'text-green-600'
                        : isCritical
                          ? 'text-red-600'
                          : 'text-amber-600';
                      const textColor = isOverridden
                        ? 'text-green-800'
                        : isCritical
                          ? 'text-red-800'
                          : 'text-amber-800';
                      const subTextColor = isOverridden
                        ? 'text-green-700'
                        : isCritical
                          ? 'text-red-700'
                          : 'text-amber-700';
                      const refColor = isOverridden
                        ? 'text-green-600'
                        : isCritical
                          ? 'text-red-600'
                          : 'text-amber-600';

                      return (
                        <div
                          key={index}
                          className={`rounded-lg p-3 border flex items-start justify-between ${bgColor}`}
                        >
                          <div className="flex items-start gap-2 flex-1">
                            {isOverridden ? (
                              <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${iconColor}`} />
                            ) : (
                              <AlertTriangle className={`h-4 w-4 flex-shrink-0 mt-0.5 ${iconColor}`} />
                            )}
                            <div>
                              <p className={`text-sm font-medium ${textColor}`}>
                                {gap.cert}
                                {isCritical && !isOverridden && (
                                  <span className="ml-2 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded">BLOCKS DEPARTURE</span>
                                )}
                              </p>
                              <p className={`text-xs ${subTextColor}`}>{gap.message}</p>
                              <p className={`text-xs mt-1 italic ${refColor}`}>{gap.wbc3Ref}</p>
                              {isOverridden && (
                                <p className="text-xs text-green-700 mt-1">
                                  ✓ Overridden — see voyage record for details
                                </p>
                              )}
                            </div>
                          </div>
                          {isCritical && !isOverridden && (
                            <button
                              onClick={() => handleOpenOverride({
                                type: 'collective',
                                key: gap.cert,
                                name: gap.cert,
                                crewId: null,
                                crewName: null,
                                wbc3Ref: gap.wbc3Ref
                              })}
                              className="ml-3 px-3 py-1.5 text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 rounded-lg flex items-center gap-1 flex-shrink-0"
                            >
                              <ShieldX className="h-3.5 w-3.5" />
                              Override
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Passengers */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Passengers
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.passengerCount}
                  onChange={(e) => updateField('passengerCount', parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                />
              </div>

              {/* FT-149: Passenger Safety Briefing Card - Only when passengers > 0 */}
              {formData.passengerCount > 0 && (
                <div
                  className={`rounded-lg p-4 mb-4 border-2 transition-colors ${
                    passengerBriefingConfirmed
                      ? 'bg-green-50 border-green-300'
                      : 'bg-amber-50 border-amber-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`flex-shrink-0 mt-0.5 ${
                      passengerBriefingConfirmed ? 'text-green-600' : 'text-amber-600'
                    }`}>
                      <Users className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        passengerBriefingConfirmed ? 'text-green-800' : 'text-amber-800'
                      }`}>
                        Passenger Safety Briefing
                      </p>
                      <p className={`text-sm mt-1 ${
                        passengerBriefingConfirmed ? 'text-green-700' : 'text-amber-700'
                      }`}>
                        {formData.passengerCount} passenger{formData.passengerCount !== 1 ? 's' : ''} on board — briefing required before departure
                      </p>
                      <label className="flex items-center gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={passengerBriefingConfirmed}
                          onChange={(e) => setPassengerBriefingConfirmed(e.target.checked)}
                          className={`h-5 w-5 rounded border-2 ${
                            passengerBriefingConfirmed
                              ? 'text-green-600 border-green-400 focus:ring-green-500'
                              : 'text-amber-600 border-amber-400 focus:ring-amber-500'
                          }`}
                        />
                        <span className={`text-sm font-medium ${
                          passengerBriefingConfirmed ? 'text-green-700' : 'text-amber-700'
                        }`}>
                          Passenger safety briefing completed
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Shore Contact */}
              <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.shoreNotified}
                  onChange={(e) => updateField('shoreNotified', e.target.checked)}
                  className="h-5 w-5 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                />
                <span className="flex items-center gap-2 text-sm text-gray-700">
                  <Phone className="h-4 w-4 text-gray-400" />
                  Shore contact notified
                </span>
              </label>
            </div>

            {/* ============================================ */}
            {/* WEATHER CARD - INLINE BIG BUTTONS */}
            {/* ============================================ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                <Cloud className="h-5 w-5 text-gray-400" />
                Departure Weather
              </h3>

              <WeatherChecklistItem
                value={weather}
                onChange={setWeather}
                hideLabel={true}
              />
            </div>

            {/* ============================================ */}
            {/* FUEL CARD */}
            {/* ============================================ */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-4">
                <Fuel className="h-5 w-5 text-gray-400" />
                Departure Fuel <span className="text-red-500">*</span>
              </h3>

              <FuelChecklistItem
                label="Select fuel level"
                value={fuelData}
                onChange={handleFuelChange}
                required={true}
                disabled={false}
              />

              {errors.fuel && !fuelData && (
                <p className="mt-2 text-sm text-red-500">{errors.fuel}</p>
              )}
            </div>

            {/* ============================================ */}
            {/* PRE-DEPARTURE CHECKS CARD (Full Width) */}
            {/* ============================================ */}
            <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckSquare className="h-5 w-5 text-cyan-600" />
                  <h3 className="font-medium text-gray-900">Pre-Departure Checks</h3>
                </div>
                <span className={`text-sm font-medium ${allRequiredChecksComplete ? 'text-green-600' : 'text-amber-600'
                  }`}>
                  {requiredCompleted}/{requiredCheckIds.length} required
                </span>
              </div>

              {/* Progress Bar */}
              <div className="px-4 pt-3">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-300 ${allRequiredChecksComplete ? 'bg-green-500' : 'bg-cyan-500'
                      }`}
                    style={{ width: `${requiredCheckIds.length > 0 ? (requiredCompleted / requiredCheckIds.length) * 100 : 0}%` }}
                  />
                </div>
              </div>

              {/* Required Checks */}
              <div className="p-4 space-y-2">
                {requiredChecks.map(check => renderCheckItem(check))}
              </div>

              {/* Optional Checks (Collapsible) */}
              {optionalChecks.length > 0 && (
                <>
                  <button
                    onClick={() => setShowOptional(!showOptional)}
                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 border-t border-gray-100"
                  >
                    <span className="text-sm text-gray-500">
                      Optional Checks ({optionalCompleted}/{optionalChecks.length})
                    </span>
                    {showOptional ? (
                      <ChevronUp className="h-4 w-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    )}
                  </button>

                  {showOptional && (
                    <div className="px-4 pb-4 space-y-2">
                      {optionalChecks.map(check => renderCheckItem(check))}
                    </div>
                  )}
                </>
              )}

              {errors.checks && (
                <div className="px-4 pb-4">
                  <p className="text-sm text-red-500">{errors.checks}</p>
                </div>
              )}
            </div>

            {/* ============================================ */}
            {/* FT-150: PRE-DEPARTURE NOTES (Full Width) */}
            {/* ============================================ */}
            <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre-Departure Notes (optional)
              </label>
              <textarea
                value={formData.preDepartureNotes}
                onChange={(e) => updateField('preDepartureNotes', e.target.value)}
                placeholder="Enter any pre-departure notes, observations, or special instructions..."
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                These notes will be saved with the voyage record and visible in voyage reports.
              </p>
            </div>

            {/* ============================================ */}
            {/* TEMPLATE FIELDS CARD (Full Width) - Phase 6 */}
            {/* Only shown if phase operation has custom fields */}
            {/* ============================================ */}
            {templateFields.length > 0 && (
              <div className="md:col-span-2">
                <TemplateFieldsRenderer
                  fields={templateFields}
                  values={templateFieldValues}
                  onChange={handleTemplateFieldChange}
                  title="Additional Information"
                />
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Footer - Fixed at bottom */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="max-w-4xl mx-auto">
          {/* Error message */}
          {errors.submit && (
            <p className="text-center text-sm text-red-500 mb-3">{errors.submit}</p>
          )}

          <div className="flex gap-3">
            {/* Cancel */}
            <button
              onClick={onCancel}
              className="py-3 px-4 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              style={{ minHeight: '56px', minWidth: '80px' }}
            >
              Cancel
            </button>

            {/* LET GO - requires all mandatory checks */}
            <button
              onClick={handleLetGo}
              disabled={!canProceed || isSubmitting}
              className={`flex-1 py-3 px-4 rounded-lg font-medium flex items-center justify-center gap-2 ${canProceed
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              style={{ minHeight: '56px' }}
            >
              {isSubmitting ? (
                <>
                  <Clock className="h-5 w-5 animate-spin" />
                  <span className="hidden sm:inline">Processing...</span>
                </>
              ) : (
                <>
                  <Anchor className="h-5 w-5" />
                  LET GO
                </>
              )}
            </button>
          </div>

          {/* Help text when not ready */}
          {!canProceed && !isSubmitting && (
            <p className="text-center text-sm text-amber-600 mt-2">
              Complete all required fields and checks to depart
            </p>
          )}
        </div>
      </div>

      {/* FT-107: Compliance Override Modal */}
      <OverrideReasonModal
        isOpen={overrideModalOpen}
        onClose={() => {
          setOverrideModalOpen(false);
          setCurrentBlocker(null);
        }}
        onConfirm={handleSubmitOverride}
        blocker={currentBlocker}
        skipperName={skipperName}
      />

      {/* Sub-ticket B: Voyage Plan Picker bottom-sheet */}
      <VoyagePlanPickerSheet
        isOpen={planPickerOpen}
        onClose={() => setPlanPickerOpen(false)}
        plans={activePlans}
        selectedPlanId={selectedPlanId}
        onPick={handlePickPlan}
      />

      {/* FT-285 Item 1: TimePickerModal mount removed — the Review screen's
          InlineTimeField now collects event-time before submit. */}
    </div>
  );
};

export default StartVoyageFullScreen;
